import React, { useState, useEffect } from "react";
import { Box, Typography, Dialog, IconButton } from "@mui/material";
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import TabsComponent from "./TabsComponent";
import APIDocs from "./APIDocs";
import EntityResponse from "./EntityResponse";
import EntityVariables from "./EntityVariables";
import dayjs from "dayjs";
import { useDispatch, connect } from "react-redux";
import { setResponseVariables } from 'redux-store/responseVariablesReducers/responseVariableSlice';
import { buildResponseVariablesForTooltip } from 'utils/responseVariableUtils';

const TestCaseSetEntityResponse = ({ testCase, isExpanded, selectedEnvironmentId, testCaseSetList }) => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const tabLabels = ["Req / Resp", "Variables", "API Docs"];

  useEffect(() => {
    if (testCase && testCaseSetList) {
      const tooltipVariables = buildResponseVariablesForTooltip(testCaseSetList, testCase?.id);
      dispatch(setResponseVariables(tooltipVariables));
    }
  }, [testCase?.id, testCaseSetList, dispatch]);

  const handleTabChange = (event, tabIndex) => {
    setActiveTab(tabIndex);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 1:
        return <EntityVariables testCaseSetId={testCase?.id} selectedEnvironmentId={selectedEnvironmentId} testCaseSet={testCase} />;
      case 2:
        return <APIDocs apiDocs={testCase?.entity?.summary?.overview} />;
      default:
        return <EntityResponse response={testCase?.test_case_set_response} />;
    }
  };

  const renderEnvironmentName = () => {
    if (activeTab !== 0) return null;
    return (
      <Typography sx={{ mt: 2 }}>
        <Typography className="break-words">
          <Typography component="span" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
            {testCase?.test_case_set_response.request_type}{' '}
          </Typography>
          {testCase?.test_case_set_response.request_url}
        </Typography>
        <Typography>
          <Typography component="span" sx={{ fontWeight: 'bold' }}>
            Environment Used:{' '}
          </Typography>
          {testCase?.test_case_set_response.environment_set?.title}
        </Typography>
        <Typography>
          <Typography component="span" sx={{ fontWeight: 'bold' }}>
            Fetched At:{' '}
          </Typography>
          {testCase?.test_case_set_response?.created_at ? dayjs(testCase?.test_case_set_response?.created_at * 1000).fromNow() : 'NA'}
        </Typography>
        <Typography>
          <Typography component="span" sx={{ fontWeight: 'bold' }}>
            Status:{' '}
          </Typography>
          {testCase?.test_case_set_response.status}
        </Typography>
      </Typography>
    );
  };

  const content = (
    <Box
      sx={{
        p: 1.5,
        bgcolor: "var(--background-primary)",
        borderRadius: isFullscreen ? "0" : "8px",
        width: isFullscreen ? "100%" : "calc(100% - 16px)",
        ml: isFullscreen ? "0" : "15px",
        height: isFullscreen ? "100%" : "520px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        border: "1px solid var(--borderColor)"
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <TabsComponent
          tabLabels={tabLabels}
          activeTab={activeTab}
          handleTabChange={handleTabChange}
        />
        <IconButton onClick={toggleFullscreen} size="small" sx={{ mr: 1 }}>
          {isFullscreen ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
        </IconButton>
      </Box>
      <Typography sx={{ mt: 2, mb: -2 }}>
        {renderEnvironmentName()}
      </Typography>
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        {renderTabContent()}
      </Box>
    </Box>
  );

  return isFullscreen ? (
    <Dialog
      open={isFullscreen}
      onClose={toggleFullscreen}
      maxWidth={false}
      PaperProps={{
        sx: {
          bgcolor: "var(--background-primary)",
          width: "80vw",
          height: "75vh",
          maxHeight: "90vh",
          borderRadius: "10px"
        }
      }}
    >
      {content}
    </Dialog>
  ) : content;
};

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    testCaseSetList: state.testCaseSet.testCaseSetList,
  }
}

export default connect(mapStateToProps)(TestCaseSetEntityResponse);
